import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  Package,
  Building2,
  Cpu,
  ClipboardCheck,
  Search,
} from 'lucide-react'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  globalSearchService,
  type GlobalSearchResult,
  type SearchResultType,
} from '@/services/globalSearchService'

// -------------------------------------------------------------------
// Constants
// -------------------------------------------------------------------

const DEBOUNCE_MS = 300

const TYPE_META: Record<SearchResultType, { label: string; icon: typeof FileText; colour: string }> = {
  document: { label: 'Documents', icon: FileText, colour: 'bg-blue-100 text-blue-700' },
  product: { label: 'Products', icon: Package, colour: 'bg-purple-100 text-purple-700' },
  supplier: { label: 'Suppliers', icon: Building2, colour: 'bg-orange-100 text-orange-700' },
  component: { label: 'Components', icon: Cpu, colour: 'bg-green-100 text-green-700' },
  checklist: { label: 'Checklists', icon: ClipboardCheck, colour: 'bg-yellow-100 text-yellow-700' },
}

const ALL_TYPES: SearchResultType[] = ['document', 'product', 'supplier', 'component', 'checklist']

// -------------------------------------------------------------------
// Hook: useGlobalSearchShortcut
// -------------------------------------------------------------------

export function useGlobalSearchShortcut(onOpen: () => void) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Only trigger on Cmd/Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        // Don't trigger if focus is inside a dropdown menu, dialog, or popover
        const target = e.target as HTMLElement | null
        if (target?.closest('[role="menu"]') || target?.closest('[role="dialog"]') || target?.closest('[data-radix-popper-content-wrapper]')) {
          return
        }
        e.preventDefault()
        onOpen()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onOpen])
}

// -------------------------------------------------------------------
// Search trigger button (for the header)
// -------------------------------------------------------------------

export function GlobalSearchTrigger({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="relative h-9 w-9 p-0 md:h-9 md:w-60 md:justify-start md:px-3 md:py-2"
      onClick={onClick}
    >
      <Search className="h-4 w-4 md:mr-2" />
      <span className="hidden md:inline-flex text-muted-foreground text-sm">
        Search everything…
      </span>
      <kbd className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 md:flex">
        <span className="text-xs">⌘</span>K
      </kbd>
    </Button>
  )
}

// -------------------------------------------------------------------
// Main GlobalSearch component (command palette)
// -------------------------------------------------------------------

export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GlobalSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeFilter, setActiveFilter] = useState<SearchResultType | null>(null)
  const navigate = useNavigate()
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Register keyboard shortcut
  const handleOpen = useCallback(() => setOpen(true), [])
  useGlobalSearchShortcut(handleOpen)

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // small delay so animation finishes before clearing
      const t = setTimeout(() => {
        setQuery('')
        setResults([])
        setActiveFilter(null)
      }, 200)
      return () => clearTimeout(t)
    }
  }, [open])

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query.trim()) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const opts = activeFilter ? { types: [activeFilter] } : undefined
        const res = await globalSearchService.search(query, opts)
        setResults(res)
      } catch (err) {
        console.error('Global search error:', err)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, activeFilter])

  // Navigate to a result
  const handleSelect = useCallback(
    (result: GlobalSearchResult) => {
      setOpen(false)
      navigate(result.route)
    },
    [navigate],
  )

  // Group results by type
  const grouped = results.reduce<Record<SearchResultType, GlobalSearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = []
    acc[r.type].push(r)
    return acc
  }, {} as Record<SearchResultType, GlobalSearchResult[]>)

  const typeOrder: SearchResultType[] = ['document', 'product', 'supplier', 'component', 'checklist']
  const orderedGroups = typeOrder.filter(t => grouped[t]?.length > 0)

  return (
    <>
      <GlobalSearchTrigger onClick={handleOpen} />

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Search documents, products, suppliers…"
          value={query}
          onValueChange={setQuery}
        />

        {/* Type filter chips */}
        <div className="flex items-center gap-1.5 border-b px-3 py-2">
          <button
            onClick={() => setActiveFilter(null)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
              activeFilter === null
                ? 'bg-certify-100 text-certify-700'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            All
          </button>
          {ALL_TYPES.map(t => {
            const meta = TYPE_META[t]
            return (
              <button
                key={t}
                onClick={() => setActiveFilter(activeFilter === t ? null : t)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                  activeFilter === t
                    ? meta.colour
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {meta.label}
              </button>
            )
          })}
        </div>

        <CommandList className="max-h-[400px]">
          {/* Empty / loading states */}
          {!loading && query.trim() && results.length === 0 && (
            <CommandEmpty>
              <div className="flex flex-col items-center gap-2 py-4">
                <Search className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">No results found for &ldquo;{query}&rdquo;</p>
                <p className="text-xs text-muted-foreground/70">
                  Try different keywords or remove filters
                </p>
              </div>
            </CommandEmpty>
          )}

          {loading && query.trim() && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Searching…
            </div>
          )}

          {/* Hint when no query yet */}
          {!query.trim() && (
            <div className="py-8 text-center">
              <Search className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                Start typing to search across everything
              </p>
              <p className="mt-1 text-xs text-muted-foreground/60">
                Documents, products, suppliers, components, checklists
              </p>
            </div>
          )}

          {/* Grouped results */}
          {orderedGroups.map((type, gi) => {
            const meta = TYPE_META[type]
            const Icon = meta.icon
            const items = grouped[type]

            return (
              <div key={type}>
                {gi > 0 && <CommandSeparator />}
                <CommandGroup heading={meta.label}>
                  {items.map(result => (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      value={`${result.type}-${result.id}-${result.name}`}
                      onSelect={() => handleSelect(result)}
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${meta.colour}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium">{result.name}</p>
                        {result.subtitle && (
                          <p className="truncate text-xs text-muted-foreground">{result.subtitle}</p>
                        )}
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        {meta.label.slice(0, -1)}
                      </Badge>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </div>
            )
          })}
        </CommandList>
      </CommandDialog>
    </>
  )
}
